import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { PricingDetail, InstantEpriceContainer } from 'wsm-pricing-display';
import { useDealerOptions } from 'wsm-pricing-data';
import { ErrorBoundary } from 'wsm-error-boundary';
import { ButtonList } from 'wsm-vehicle-cta-display';
import { Tabs, Tab } from 'react-bootstrap';
import { useRequestData, usePrefs, useLabels, useFlags } from 'wsm-common-data';
import { trackEvent } from 'ddc-track-event';
import { DetailedIncentives, filterIncentiveIds } from 'wsm-incentive-display';

import VehicleAttributeList from './VehicleAttributeList';
import Callout from './Callout';
import VehiclePackages from './VehiclePackages';
import { getVehicleObj } from '../utilities/vehicleTracking';
import { getTargetLocations } from '../utilities/getTargetLocations';

const VehicleCardData = ({
	accountId,
	accountInfo,
	incentives,
	isGridLayout,
	isTabbedLayout,
	vehicle
}) => {
	const {
		buttonSizeEPrice,
		buttonStyleEPrice,
		ePriceFontWeight,
		incentiveDisplayGrouping,
		incentiveTitleCustom,
		incentiveTitleDisplay,
		instantEpricePreventMultipleEpriceLeads,
		instantEpriceShowAllAfterLeadSubmit,
		restrictInsertLocationsForVehicles,
		showDealerConditionalIncentives,
		showDealerRegularIncentives,
		showIncentives,
		showInstantEpriceForSharedInventory,
		showOemConditionalIncentives,
		showOemNationalIncentives,
		showPackages,
		tabInfoLabel,
		tabOrder,
		tabPricingLabel,
		tabSpecialsLabel,
		hideInformationTabs
	} = usePrefs();

	const {
		accountId: vehicleAccountId,
		attributes = [],
		bestIncentiveIds = [],
		callout: callouts,
		condition,
		incentiveIds = [],
		inventoryButtons = [],
		link,
		packages = [],
		pricing,
		title,
		uuid
	} = vehicle;

	const incentiveIdList = incentiveIds || bestIncentiveIds || [];

	const flags = useFlags();
	const [
		shouldRequestDealerOptions,
		setShouldRequestDealerOptions
	] = useState(false);
	const [isDisplayingEprice, setIsDisplayingEPrice] = useState(false);
	const { userProfile } = useSelector(state => state);
	const { pageAlias } = useSelector(state => state.widgetInfo);
	const { widgetName, windowId, locale } = useRequestData();
	const labels = useLabels();
	const dealerOptions = useDealerOptions(
		uuid,
		locale,
		shouldRequestDealerOptions,
		widgetName
	);

	const hideInformationTabsEnabled = flags['hide-information-tabs-enabled'];
	const tabArray = [];
	const orderedTabs = tabOrder
		.replace(/\s/g, '')
		.toLowerCase()
		.split(',');
	const tabLabels = {
		info: labels.get(tabInfoLabel),
		specials: labels.get(tabSpecialsLabel),
		pricing: labels.get(tabPricingLabel)
	};

	const handleDealerOptionsMouseEnter = () => {
		setShouldRequestDealerOptions(true);
	};

	const showEPrice = id => {
		setIsDisplayingEPrice(id === uuid && id !== 'none');
	};

	const ePriceButton = !isDisplayingEprice ? (
		<InstantEpriceContainer
			accountId={accountId}
			buttonSize={buttonSizeEPrice}
			buttonStyle={buttonStyleEPrice}
			ePriceFontWeight={ePriceFontWeight}
			instantEpricePreventMultipleEpriceLeads={
				instantEpricePreventMultipleEpriceLeads
			}
			instantEpriceShowAllAfterLeadSubmit={
				instantEpriceShowAllAfterLeadSubmit
			}
			key="eprice"
			pageAlias={pageAlias}
			pricing={pricing}
			showEPrice={id => showEPrice(id)}
			showInstantEpriceForSharedInventory={
				showInstantEpriceForSharedInventory
			}
			userProfile={userProfile}
			vehicleId={uuid}
			vehicleAccountId={vehicleAccountId}
		/>
	) : null;

	const filteredIncentives = filterIncentiveIds(incentives, incentiveIdList);
	const vehicleTrackingData = getVehicleObj(uuid);

	const tabComponents = {
		info: (
			<React.Fragment>
				<VehicleAttributeList attributes={attributes} />
				{isTabbedLayout && (
					<React.Fragment>
						{showPackages === 'true' && (
							<VehiclePackages packages={packages} link={link} />
						)}
						<Callout
							callouts={callouts}
							vehicleTitle={title}
							isTabbedLayout={isTabbedLayout}
						/>
					</React.Fragment>
				)}
			</React.Fragment>
		),
		specials:
			showIncentives === 'true' && filteredIncentives.length ? (
				<DetailedIncentives
					incentives={filteredIncentives}
					accountInfo={accountInfo}
					incentiveTitleDisplay={incentiveTitleDisplay}
					incentiveTitleCustom={incentiveTitleCustom}
					incentiveDisplayGrouping={incentiveDisplayGrouping}
					enableAccordion={!isTabbedLayout}
					showDealerRegularIncentives={
						showDealerRegularIncentives === 'true'
					}
					showDealerConditionalIncentives={
						showDealerConditionalIncentives === 'true'
					}
					showOemNationalIncentives={
						showOemNationalIncentives === 'true'
					}
					showOemConditionalIncentives={
						showOemConditionalIncentives === 'true'
					}
				/>
			) : null,
		pricing: (
			<ErrorBoundary newRelicPageAction="WS INV LISTING ERROR BOUNDARY">
				<PricingDetail
					{...{
						pricing,
						incentives,
						accountInfo,
						accountId,
						condition,
						isGridLayout,
						userProfile
					}}
					fetchDealerOptionsCallback={handleDealerOptionsMouseEnter}
					dealerOptions={dealerOptions}
					vehicleAccountId={vehicleAccountId}
					vehicleId={uuid}
					isDisplayingEprice={isDisplayingEprice}
				/>
			</ErrorBoundary>
		)
	};

	const targetLocations = getTargetLocations(title, restrictInsertLocationsForVehicles);
	const hasInventoryButtons = inventoryButtons && inventoryButtons.length > 0;

	const ctaButtons = targetLocations.has('vehicle-ctas')
	? [
		<ButtonList
			buttons={[]}
			key="inventoryButtons"
			vehicleTrackingData={undefined}
			restrictedLocations={targetLocations}
		/>
	]
	: [
		ePriceButton,
		<ButtonList
			buttons={inventoryButtons}
			key="inventoryButtons"
			vehicleTrackingData={hasInventoryButtons ? vehicleTrackingData : undefined}
			restrictedLocations={targetLocations}
		/>
	];

	orderedTabs.map(
		key =>
			tabComponents[key] &&
			tabArray.push({
				eventKey: key,
				key,
				title: tabLabels[key],
				component: tabComponents[key]
			})
	);

	const VEHICLE_PRICING = 'vehicle-pricing';
	const VEHICLE_PRICING_CUSTOM = `${VEHICLE_PRICING}-custom`;

	let VEHICLE_PRICING_LOCATION = restrictInsertLocationsForVehicles ? `${VEHICLE_PRICING} ${VEHICLE_PRICING_CUSTOM}` : VEHICLE_PRICING;

	if (targetLocations.has(VEHICLE_PRICING)) {
		VEHICLE_PRICING_LOCATION = VEHICLE_PRICING_CUSTOM;
	}

	return (
		<React.Fragment>
			{isTabbedLayout ? (
				<React.Fragment>
					<Tabs
						className="vehicle-card-tabs tabs"
						id={uuid}
						onSelect={key =>
							trackEvent(widgetName, windowId, {
								element: `${tabLabels[key]} tab`,
								result: 'Tab displayed'
							})
						}
					>
						{tabArray
							.filter(tab => {
								return (
									!hideInformationTabsEnabled ||
									!hideInformationTabs
										?.toLowerCase()
										.includes(tab.key)
								);
							})
							.map(tab => (
								<Tab
									eventKey={tab.eventKey}
									key={tab.key}
									title={tab.title}
									tabClassName="w-100 text-center"
								>
									{tab.component}
								</Tab>
							))}
					</Tabs>
					{/* to make sure VehicleTitle always displays pricing */}
					{hideInformationTabs?.toLowerCase().includes('pricing') && (
						<div className="d-none">{tabComponents.pricing}</div>
					)}
					{ctaButtons}
					<div
						className="hidden vehicle-card-pricing mt-3"
						data-location={VEHICLE_PRICING_LOCATION}
					/>
				</React.Fragment>
			) : (
				<div className="d-md-flex">
					<div className="vehicle-card-info-packages w-100">
						{tabComponents.info}
						{showPackages === 'true' && (
							<VehiclePackages packages={packages} link={link} />
						)}
					</div>
					<div className="vehicle-card-pricing w-100">
						<div className="w-100">{tabComponents.pricing}</div>
						<div className="w-100">{tabComponents.specials}</div>
						<div className="w-100">{ctaButtons}</div>
						<div className="w-100">
							<div
								className="hidden vehicle-card-pricing mt-3"
								data-location={VEHICLE_PRICING_LOCATION}
							/>
						</div>
					</div>
				</div>
			)}
		</React.Fragment>
	);
};

VehicleCardData.propTypes = {
	accountId: PropTypes.string.isRequired,
	accountInfo: PropTypes.shape({}),
	accounts: PropTypes.shape({}),
	incentives: PropTypes.shape({}),
	isGridLayout: PropTypes.bool,
	isTabbedLayout: PropTypes.bool.isRequired,
	vehicle: PropTypes.shape({
		accountId: PropTypes.string,
		attributes: PropTypes.arrayOf(PropTypes.object),
		bestIncentiveIds: PropTypes.arrayOf(PropTypes.string),
		callout: PropTypes.arrayOf(PropTypes.object),
		condition: PropTypes.string,
		incentiveIds: PropTypes.arrayOf(PropTypes.string),
		inventoryButtons: PropTypes.arrayOf(PropTypes.object),
		link: PropTypes.string,
		packages: PropTypes.arrayOf(PropTypes.string),
		pricing: PropTypes.shape({}),
		title: PropTypes.string,
		uuid: PropTypes.string
	}).isRequired
};

VehicleCardData.defaultProps = {
	accounts: {},
	incentives: {}
};

export default VehicleCardData;
